import styled from 'styled-components';
import { PulseLoader } from 'react-spinners';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import Portal from '../../../components/Portal';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IComCdList, Time } from 'customTypes';
import { useTranslation } from 'react-i18next';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import RangePicker, { onChangeRangeInput } from '../../../components/RangePicker';
import useDateRange from '../../../hooks/useDateRange';
import ShortcutButton from '../../../components/button/ShortcutButton';
import { BtnGhost, InitBtn } from '../../../components/Button';
import SelectBox from '../../../components/SelectBox';
import { INIT_END_HMS, INIT_HMS, INIT_SCHEDULE, INIT_START_HMS, LIST_COUNT, SCHEDULE_TAB_LIST } from '../../../_constants';
import { setComCdListState } from '../../../utils/setComCdListState';
import useSetListCount from '../../../utils/useSetListCount';
import Input from '../../../components/Input';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import TuiGrid from '../../../components/Table/TuiGrid';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import i18n from '../../../translation/i18n';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import { logPost } from '../../../services/log';
import { useSetAuth } from '../../../utils/useSetAuth';
import { useGetFrozenCount } from '../../../utils/getFrozenCount';
import GetFetchStatus from '../../../hooks/GetFetchStatus';
import { todayYYYYMMDD } from '../../../utils/formatDate';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../../services/_common';
import { toast } from 'react-toastify';
import { useOutletContext } from 'react-router-dom';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';
import { showExcelButton } from '../../../utils/deviceAuth';
import { v4 } from 'uuid';
import SearchSelectBoxSm from '../../../components/SearchSelectBoxSm';
import { isMobile } from '../../../utils/checkMobile';
import CustomImageRenderer from '../../../components/CustomImageRenderer';
import ServiceReportModal from '../../../components/Modal/ServiceReportModal';

interface IAddress {
  address1: string;
  postNum: string;
  latitude: string;
  longitude: string;
}

const Root = styled(ContentsContainerRoot)`
  .text-tertiary {
    color: var(--text-tertiary);
  }
  .custom-tag {
    & > span {
      font-size: 0.75rem;
    }
  }
  .custom-box {
    display: flex;
    justify-content: start;
    gap: 0.5rem;
    > div {
      color: var(--text-tertiary);
    }
  }
`;

const isMobileDevice = isMobile();

const ReportList = () => {
  const userInfo = useRecoilValue(userState);
  const { eExcelYn, eListYn } = userInfo;
  const { auth } = useSetAuth();
  const toDay = todayYYYYMMDD();
  const size = useOutletContext<any>();
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: '', index: 0 });
  const [visibleRangePicker, setVisibleRangePicker] = useState<boolean>(false);
  const { dateRange, setDateRange, userInputDateRange, setUserInputDateRange, initiateDate } = useDateRange('month');
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const [searchOption, setSearchOption] = useState({ search: '' });
  const [searchHsCd1, setSearchHsCd1] = useState({ type: 'hsCd1', hsCd1: '', cdName: t('본사 선택') });
  const frozenCount = useGetFrozenCount();
  const [columns, setColumns] = useState<any>(null);
  const [excelBtn, setExcelBtn] = useState<boolean>(false);
  const [rowKey, setRowKey] = useState('');
  const [selectEvent, setSelectEvent] = useState(INIT_SCHEDULE); // 선택된 일정 데이터
  const [dateTime, setDateTime] = useState<any>({ start: toDay, end: toDay }); // 날짜
  const [rangeState, setRangeState] = useState<any>([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  const [selectedTimeStart, setSelectedTimeStart] = useState(INIT_START_HMS);
  const [selectedTimeEnd, setSelectedTimeEnd] = useState(INIT_END_HMS);
  const [availableTime, setAvailableTime] = useState<[{ start: Time; end: Time }]>([{ start: INIT_START_HMS, end: INIT_END_HMS }]);
  const [availableTimeRange, setAvailableTimeRange] = useState<{ start: Time; end: Time }>({ start: INIT_START_HMS, end: INIT_END_HMS });
  const [csCd, setCsCd] = useState({ type: 'csCd', csCd: '', cdName: t('공유항목 선택') }); // 공유 항목
  const [wGubun, setWGubun] = useState({ type: 'wGubun', v: '0', cdName: t('매주') }); // 반복일정 - 주간 - 기본값 매주
  const [users, setUsers] = useState<{ label: string; value: string }[]>([]);
  const smNameRef = useRef<HTMLDivElement>(null);
  const siteManagerNameRef = useRef<HTMLInputElement>(null);
  const siteManagerPhoneRef = useRef<HTMLInputElement>(null);
  // const contentsRef = useRef<HTMLInputElement>(null);
  const userNameRef = useRef<HTMLInputElement>(null);
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false); // 필수값 여부
  const [address, setAddress] = useState<IAddress>({ address1: '', postNum: '', latitude: '', longitude: '' }); // 주소 정보
  const [tableState, setTableState] = useState<any>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const [hsCd1, setHsCd1] = useState({ type: 'hsCd1', hsCd1: '', cdName: t('본사 선택') }); // 현장명 - 본사
  const [hsCd2, setHsCd2] = useState({ type: 'hsCd2', hsCd2: '', cdName: t('현장 선택') }); // 현장명 - 현장
  const [carInfoList, setCarInfoList] = useState<any[]>([]);
  const [ccCd, setCcCd] = useState({ type: 'ccCd', ccCd: '', cdName: t('차량 선택') }); // 차량 정보
  const [dCd, setDCd] = useState({ type: 'dCd', dCd: '', cdName: t('운전자 선택') }); // 운전자
  const [isPaid, setIsPaid] = useState<string>('Y'); // 유/무상
  const [headInfoList, setHeadInfoList] = useState<any[]>([]);
  const showExcelBtn = showExcelButton(auth.excelAuth);
  const [isNewSchedule, setIsNewSchedule] = useState<boolean>(true); // 신규 여부
  const [initScheduleInfoData, setInitScheduleInfoData] = useState<{
    travelYn: boolean;
    hsCd1: string;
    hsCd2: string;
    isPaid: string;
    siteManagerName: string;
    siteManagerPhone: string;
    siteManagerPartname: string;
  }>({ travelYn: false, hsCd1: '', hsCd2: '', isPaid: 'Y', siteManagerName: '', siteManagerPhone: '', siteManagerPartname: '' }); // 초기 선택된 해당 일정 정보
  const [shouldRefetchAfterInit, setShouldRefetchAfterInit] = useState(false);
  const [siteManagerName, setSiteManagerName] = useState<string>('');
  const [siteManagerPhone, setSiteManagerPhone] = useState<string>('');
  const [siteManagerPartname, setSiteManagerPartname] = useState<string>('');
  const isMobileView = isMobile();

  const {
    data: schedules,
    isLoading: isLoadingSchedules,
    isFetching: isFetchingSchedules,
    refetch: refetchSchedule,
  } = GetFetchStatus({
    path: '/schedule/reports',
    req: {
      cCd: userInfo.hCd,
      date1: userInputDateRange.start?.replaceAll('-', ''),
      date2: userInputDateRange.end?.replaceAll('-', ''),
      hCd: `${searchHsCd1.hsCd1}`,
      search: searchOption.search?.trim(),
    },
    hCdOnly: { value: true, key: 'cCd' },
  });

  // 사내 - 본사 정보 조회
  const getHeadInfo = async () => {
    const req = { cCd: userInfo.hCd };
    const res = await apiGet({ path: '/company/chead', req });
    if (res.status === 200) {
      const defaultSet = {
        type: 'hsCd1',
        hsCd1: '',
        cdName: t('본사 선택'),
      };
      const newMap = res.data.data.map((el: any) => ({ ...el, hsCd1: el.hCd, cdName: el.hName }));
      // cdName ㄱㄴㄷ 순 정렬
      setHeadInfoList([defaultSet, ...newMap.sort((a: any, b: any) => a.cdName.localeCompare(b.cdName))]);
    }
  };

  useEffect(() => {
    getCarInfo();
    getHeadInfo();
  }, []);

  // 차량 리스트 조회
  const getCarInfo = async () => {
    const req = { cCd: userInfo.hCd };
    const res = await apiGet({ path: '/schedule/car', req });

    if (res.status === 200) {
      const newMap = res.data.data.map((el: any) => ({ ...el, cdName: el.ccName }));
      setCarInfoList(newMap);
    }
  };

  const scheduleGubunMap = useMemo(() => {
    return new Map(SCHEDULE_TAB_LIST.map((item) => [item.v, item]));
  }, []);

  useEffect(() => {
    if (schedules) {
      const newSchedules = schedules
        .filter((el: any) => el.travelYn === 'Y')
        .map((el: any) => {
          return {
            ...el,
            isPaid: el.isPaid === 'Y' ? t('유상') : t('무상'),
            attenders: el.userId?.map((el2: any) => el2.label).join(','),
            installList: el.installList || [],
          };
        });
      setTableState(newSchedules);
    }
  }, [schedules, scheduleGubunMap, userInfo.gCd]);

  useEffect(() => {
    setComCdListState(LIST_COUNT, setListCountComCdList, false);
  }, []);

  useEffect(() => {
    if (tableState) {
      setColumns([
        {
          header: t('유/무상'),
          name: 'isPaid',
          align: 'start',
          width: 60,
          filter: 'select',
        },
        {
          header: t('본사명'),
          name: 'hName',
          align: 'start',
          width: 80,
          filter: 'select',
        },
        {
          header: t('현장명'),
          name: 'sName',
          align: 'start',
          width: 120,
          filter: 'select',
        },
        {
          header: t('유지보수'),
          name: 'maintenanceTitle',
          align: 'start',
          minWidth: 200,
          filterable: true,
        },
        {
          header: t('설치/해체'),
          name: 'installTitle',
          align: 'start',
          minWidth: 200,
          sortable: true,
        },
        {
          header: t('참가자'),
          name: 'attenders',
          align: 'start',
          width: 140,
        },
        {
          header: t('완료일자'),
          name: 'completeDate',
          align: 'center',
          width: 100,

          sortable: true,
        },
        {
          header: t('서명'),
          name: 'signImg',
          align: 'center',
          renderer: { type: CustomImageRenderer },
          width: 120,
          sortable: true,
        },
      ]);
    }
  }, [i18n.language, tableState]);

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  const onClickSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    // 검색 시 가상 키보드를 내려 검색 진행 중 화면이 잘리는 현상 방지
    // (키보드 내려간 뒤 그리드 높이 복구는 useTuiGridSize의 ResizeObserver가 처리)
    if (isMobileView) (document.activeElement as HTMLElement | null)?.blur?.();
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '일정 관리',
      action: '조회',
      etc: `기간 : ${userInputDateRange.start} ~ ${userInputDateRange.end} 본사 : ${searchHsCd1.cdName} 검색어 : ${searchOption.search}`,
    });
    if (filterRef) {
      // 모바일일 경우 검색 필터 모달 창 닫기
      filterRef.current?.classList.remove('active');
      setOpenModal({ status: false, type: 'filter' });
    }

    refetchSchedule();
  };

  const onEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onClickSearch();
    }
  };

  const onClickInitiateSearchOption = () => {
    initiateDate();
    setSearchOption({ search: '' });
    setSearchHsCd1({ type: 'hsCd1', hsCd1: '', cdName: t('본사 선택') });
    setShouldRefetchAfterInit(true);
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    if (tableState?.length <= 0) return;
    setExcelBtn(true);
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '일정 관리',
      action: '엑셀 저장',
      etc: ``,
    });
  };

  // 일정 추가 클릭 핸들러
  const handleNewEvent = () => {
    if (!auth.createAuth) toast.warn(t('일정 추가 권한이 없습니다'));
    else {
      initSet();
      setIsNewSchedule(true);
      handleAddEventClick();
    }
  };

  // 일정 추가 팝업 열기/닫기 핸들러
  const handleAddEventClick = useCallback(() => {
    setOpenModal({ type: 'scheduleView', status: true });
  }, []);

  const renderExcelBtn = () => {
    if (showExcelBtn) {
      return (
        <>
          <BtnGhost onClick={handleNewEvent}>{t('일정 추가')}</BtnGhost>
          <BtnGhost onClick={onClickExcelSave}>
            <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
          </BtnGhost>
        </>
      );
    }
    return <BtnGhost onClick={handleNewEvent}>{t('일정 추가')}</BtnGhost>;
  };

  // 초기화
  const initSet = () => {
    setIsSaveClicked(false);
    setHsCd1({ type: 'hsCd1', hsCd1: '', cdName: t('본사 선택') });
    setHsCd2({ type: 'hsCd2', hsCd2: '', cdName: t('현장 선택') });
    setWGubun({ type: 'wGubun', v: '0', cdName: t('매주') });
    setCsCd({ type: 'csCd', csCd: '', cdName: t('공유항목 선택') });
    setCcCd({ type: 'ccCd', ccCd: '', cdName: t('차량 선택') });
    setDCd({ type: 'dCd', dCd: '', cdName: t('운전자 선택') });

    setDateTime({ start: toDay, end: toDay });
    setRangeState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
    setSelectedTimeStart(INIT_START_HMS);
    setSelectedTimeEnd(INIT_HMS);
    setAvailableTime([{ start: INIT_START_HMS, end: INIT_END_HMS }]);
    setAddress({ address1: '', postNum: '', latitude: '', longitude: '' });
    setUsers([]);
    setSelectEvent(INIT_SCHEDULE);
    setSiteManagerName('');
    setSiteManagerPhone('');
    setSiteManagerPartname('');
    setIsPaid('Y');
    setInitScheduleInfoData({ travelYn: false, hsCd1: '', hsCd2: '', isPaid: 'Y', siteManagerName: '', siteManagerPhone: '', siteManagerPartname: '' });
  };

  // 초기화 후 refetch 실행
  useEffect(() => {
    if (!isMobileDevice && shouldRefetchAfterInit) {
      refetchSchedule();
      setShouldRefetchAfterInit(false);
    }
  }, [shouldRefetchAfterInit, refetchSchedule]);

  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setOpenModal({
      type: 'serviceReport',
      status: true,
      state: {
        cCd: userInfo.hCd,
        hCd: hsCd1.hsCd1,
        hName: hsCd1.cdName,
        sCd: hsCd2.hsCd2,
        sName: hsCd2.cdName,
        address: address.address1,
        smIdx: rowData.smIdx,
        userId: userInfo.userId,
        smName: rowData.smName,
        isPaid,
        companyUser,
        users,
        selectedTimeStart,
        selectedTimeEnd,
        siteManagerName,
        siteManagerPhone,
        siteManagerPartname,
      },
    });
    // initSet();
    // setIsNewSchedule(false);

    // if (rowData?.smGubun !== '4') {
    //   setUsers(rowData.userId || []);
    //   setRowKey(`${filteredIndex}`);
    //   setOpenModal({ status: true, type: 'scheduleView', data: rowData, index: filteredIndex, title: '일정 정보' });
    //   setCcCd({ type: 'ccCd', ccCd: rowData.ccCd || '', cdName: rowData.ccName || t('차량 선택') });
    //   setDCd({ type: 'dCd', dCd: rowData.dId || '', cdName: rowData.dName || t('운전자 선택') });
    //   setIsPaid(rowData.isPaid);
    //   setSelectEvent({
    //     smIdx: rowData.smIdx,
    //     smGubun: rowData.smGubun,
    //     completeYn: rowData.completeYn,
    //     smName: rowData.smName,
    //     importantYn: rowData.importantYn,
    //     loopYn: rowData.dGubun ? 'Y' : 'N',
    //     fontColor: rowData.fontColor ? rowData.fontColor : '#000000',
    //     backColor: rowData.backColor ? rowData.backColor : '#f1f1f1',
    //     isRepeatSchedule: rowData.isRepeatSchedule,
    //     orgRepeatSdate: rowData.orgRepeatSdate ? rowData.orgRepeatSdate : '',
    //     setAttenders: rowData.setAttenders,
    //     alldayYn: rowData.alldayYn,
    //     contents: rowData.contents || '',
    //     repeatsmIdx: rowData.repeatsmIdx,
    //     wGubun: rowData.wGubun,
    //     dGubun: rowData.dGubun.split('|').map((gubun: string) => gubun.trim()),
    //     csCd: rowData.csCd,
    //     ccCd: rowData.ccCd || { type: 'ccCd', cdName: t('미선택') },
    //     dCd: rowData.dCd || { type: 'dCd', cdName: t('미선택') },
    //     hsCd1: rowData.hCd,
    //     hsCd2: rowData.sCd,
    //     deleteFileIndex: [],
    //     writer: rowData.writer,
    //     wDate: rowData.wDate,
    //     editor: rowData.editor,
    //     eDate: rowData.eDate,
    //     address1: rowData.address,
    //     latitude: rowData.latitude,
    //     longitude: rowData.longitude,
    //     travelYn: rowData.travelYn,
    //     users: rowData.users,
    //     siteManagerName: rowData.siteManagerName || '',
    //     siteManagerPhone: rowData.siteManagerPhone || '',
    //     siteManagerPartname: rowData.siteManagerPartname || '',
    //     fileList:
    //       rowData.scheduleFList.map((file: any) => ({
    //         fileId: file.fIndex, // 파일 고유 id
    //         file: file.fUrl,
    //         fileName: file.fUrl.slice(file.fUrl.lastIndexOf('/') + 1, file.fUrl.length), // 파일명
    //         fileFormat: file.fUrl.slice(file.fUrl.lastIndexOf('.') + 1, file.fUrl.length), // 파일 포멧 'mp4, jpg'
    //         fUrl: file.fUrl, // 파일 url
    //       })) || [], // 파일
    //   });
    //   setSiteManagerName(rowData.siteManagerName || '');
    //   setSiteManagerPhone(rowData.siteManagerPhone || '');
    //   setSiteManagerPartname(rowData.siteManagerPartname || '');
    //   setDateTime({ start: rowData.sDate, end: rowData.fDate });
    //   setWGubun((prev: any) => ({ ...prev, v: rowData.wGubun }));
    //   if (rowData.csCd !== '') setCsCd((prev: any) => ({ ...prev, csCd: rowData.csCd, cdName: rowData.csName }));
    //   setAddress({ address1: rowData.address, latitude: rowData.latitude, longitude: rowData.longitude, postNum: '' });
    //   setHsCd1({ type: 'hsCd1', hsCd1: rowData.hCd, cdName: rowData.hName });
    //   setHsCd2({ type: 'hsCd2', hsCd2: rowData.sCd, cdName: rowData.sName });
    //   const startDate = dayjs(rowData.sDate).toDate();
    //   const endDate = dayjs(rowData.fDate).toDate();
    //   setRangeState([{ startDate, endDate, key: 'selection' }]);
    //   setSelectedTimeStart(parseTimeFromHHMM(rowData.sTime) || INIT_START_HMS);
    //   setSelectedTimeEnd(parseTimeFromHHMM(rowData.fTime) || INIT_END_HMS);
    //   setInitScheduleInfoData({
    //     travelYn: rowData.travelYn === 'Y',
    //     hsCd1: rowData.hCd ?? '',
    //     hsCd2: rowData.sCd ?? '',
    //     isPaid: rowData.isPaid,
    //     siteManagerName: rowData.siteManagerName || '',
    //     siteManagerPhone: rowData.siteManagerPhone || '',
    //     siteManagerPartname: rowData.siteManagerPartname || '',
    //   });
    // } else {
    //   toast.info(rowData.smName);
    // }
  };

  // 사내 사용자 조회
  const getCompanyUserAPI = async () => {
    const req = { hCd: userInfo.hCd };
    const res = await apiGet({ path: '/user/company', req });

    if (res.status === 200) {
      const YNFilter = res.data.data.filter((el: any) => el.approvalYn === 'Y' && el.useYn === 'Y'); // 승인 및 사용중인 사용자만 필터링
      const newMap = YNFilter.map((el: any) => ({ ...el, label: el.userName, value: el.userId }));
      return newMap;
    }

    return res;
  };

  // 사내 사용자 조회
  const getCompanyUserPartAPI = async () => {
    const req = { hCd: userInfo.hCd };
    const res = await apiGet({ path: '/user/company/part', req });

    if (res.status === 200) {
      const newMap = res.data.data.map((el: any) => ({
        grCd: v4(),
        grName: el.partName,
        subList: el.users.map((el2: any) => ({ key: el2.userId, name: el2.userName })),
      }));

      return newMap;
    }
    return res;
  };

  // 일정 항목 조회
  const getScheduleInfo = async () => {
    const req = { cCd: userInfo.hCd };
    const res = await apiGet({ path: '/schedule/set/user', req });

    if (res.status === 200) {
      const writerFilter = res.data.data;
      const useYnFilter = writerFilter.filter((el: any) => el.useYn === 'Y'); // 사용유무 Y 인것만 필터링
      const newMap = useYnFilter.map((el: any) => ({ ...el, type: 'shareItem', cdName: el.csName }));
      const defaultSet = {
        type: 'shareItem',
        csCd: '',
        cdName: t('미선택'),
      };

      return [defaultSet, ...newMap];
    }

    return res;
  };

  const deleteAPI = (deleteType: string) => {
    deleteEventMutation.mutate(deleteType);
  };

  const deleteSchedule = async (deleteType: string) => {
    try {
      const isRepeat = deleteType === 'repeat';
      const path = isRepeat ? '/schedule/repeat' : '/schedule';
      const req = {
        smIdx: Number(selectEvent.smIdx),
        cCd: userInfo.hCd,
        ...(isRepeat ? { updateDate: selectEvent.orgRepeatSdate } : {}),
        ...(!isRepeat && selectEvent.repeatsmIdx ? { repeatsmIdx: selectEvent.repeatsmIdx } : {}),
      };

      const res = await apiDelete({ path, contentType: 'application/json', req });
      if (res.status === 200) {
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '일정 관리(리스트)',
          action: '일정 삭제',
          etc: `일정 제목 : ${selectEvent.smName}`,
        });
      }
      return res;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };
  // 일정 삭제 mutation
  const deleteEventMutation = useMutation({
    mutationFn: (deleteType: string) => {
      return deleteSchedule(deleteType); // API 호출
    },
    onSuccess: () => {
      refetchSchedule();
      // // 성공 시 캐시된 일정 데이터 갱신
      // queryClient.invalidateQueries({ queryKey: ['schedules'] });
      // setIsSlot(true);
      setOpenModal((prev: any) => ({ ...prev, status: false })); // 성공 시 모달 닫기
      // setPopupData((prev: any) => ({ ...prev, events: events.filter((el: any) => el.orgSdate === dayjs(prev.date).format('YYYYMMDD')) }));
      toast.success(t('일정이 삭제되었습니다'));
    },
    onError: (error) => {
      console.error('일정 삭제 실패:', error);
      toast.error(t('일정 삭제에 실패했습니다'));
    },
  });

  const saveUpdateSchedule = async (schedule: any, saveType: string) => {
    try {
      const isRepeat = saveType === 'repeat';
      const path = isRepeat ? '/schedule/repeat' : '/schedule';
      const files = { files: selectEvent.fileList.filter((el: IFile) => el.fUrl === '').map((el: IFile) => el.file) };
      const dFileIdx = { deleteFileIndex: schedule.deleteFileIndex.map((idx: number) => idx.toString()) };
      const setData = {
        smIdx: schedule.smIdx.toString(),
        cCd: userInfo.hCd,
        smGubun: schedule.smGubun,
        smName: schedule.smName,
        sDate: dateTime.start.replaceAll('-', ''),
        sTime: setTimeFormat(selectedTimeStart),
        fDate: dateTime.end.replaceAll('-', ''),
        fTime: setTimeFormat(selectedTimeEnd),
        contents: schedule.contents,
        users: selectEvent.smGubun === '2' || selectEvent.smGubun === '3' ? users.map((el: any) => el.value) : [],
        alldayYn: schedule.alldayYn,
        completeYn: schedule.completeYn,
        travelYn: selectEvent.smGubun === '3' ? schedule.travelYn : 'N',
        importantYn: schedule.importantYn,
        hsCd: `${hsCd1.hsCd1}${hsCd2.hsCd2}`,
        csCd: schedule.smGubun === '3' ? csCd.csCd : '',
        ccCd: ccCd.ccCd,
        dId: dCd.dCd,
        wGubun: selectEvent.dGubun.join('') !== '' ? selectEvent.wGubun : '',
        dGubun: schedule.dGubun.sort((a: string, b: string) => +a - +b).join('|'),
        address: address.address1,
        latitude: address.latitude,
        longitude: address.longitude,
        siteManagerName,
        siteManagerPhone,
        siteManagerPartname,
        ...(files.files.length !== 0 ? files : {}), // 파일 리스트
        ...(dFileIdx.deleteFileIndex.length !== 0 ? dFileIdx : {}), // 기존 파일 삭제한 아이디
        ...(isRepeat ? { updateDate: selectEvent.orgRepeatSdate } : {}),
      };
      const req = getReq({ ...setData });
      const res = await apiPatch({ path, contentType: 'multipart/form-data', req });
      if (res.status === 200) {
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '일정 관리(리스트)',
          action: '일정 수정',
          etc: `일정 제목 : ${schedule.smName}`,
        });
      }
      return res;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const addSchedule = async (schedule: any) => {
    try {
      const res = await apiPost({ path: '/schedule', contentType: 'multipart/form-data', req: schedule });
      const { statusCode } = res.data;
      if (statusCode === 200) {
        refetchSchedule();
        toast.success(t('일정이 추가되었습니다'));
        setOpenModal((prev: any) => ({ ...prev, status: false })); // 성공 시 모달 닫기
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '일정관리(캘린더)',
          action: '일정 추가',
          etc: `일정명 : ${schedule.get('smName')}`,
        });
      } else toast.error(t('일정 추가에 실패했습니다'));
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // 일정 업데이트 mutation
  const updateEventMutation = useMutation({
    mutationFn: (updatedEvent: any) => {
      const [uEvent, saveType] = updatedEvent;
      return saveUpdateSchedule(uEvent, saveType);
    },
    onSuccess: () => {
      refetchSchedule();
      // 성공 시 캐시된 일정 데이터 갱신
      // queryClient.invalidateQueries({ queryKey: ['schedules'] });
      // setIsSlot(true);
      setOpenModal((prev: any) => ({ ...prev, status: false })); // 성공 시 모달 닫기
      toast.success('일정이 수정되었습니다');
    },
    onError: (error) => {
      console.error('일정 업데이트 실패:', error);
      toast.error(t('일정 수정에 실패했습니다'));
    },
  });

  // 시간 포멧팅 정의 - { h, m, s } -> 'hhmm'
  const setTimeFormat = (timeObj: Time) => {
    const arr = [];
    for (const val of Object.values(timeObj)) {
      arr.push(val.toString().padStart(2, '0'));
    }

    return `${arr.join('').slice(0, 4)}`;
  };

  // 시간/분/초 => 밀리 세컨드 변환
  const calcTotalSencond = (time: Time) => {
    return time.h * 3600 + time.m * 60 + time.s;
  };

  // 시간 범위 체크 ( 종료시간이 시작시간보다 클 경우에 대한 체크 )
  const isTimeChecked = ({ start, end }: { start: Time; end: Time }) => {
    let checked = false;
    const startTotalSeconds = calcTotalSencond(start);
    const endTotalSeconds = calcTotalSencond(end);

    if (startTotalSeconds > endTotalSeconds) checked = true;

    return checked;
  };

  const getReq = (reqData: any) => {
    const formData = new FormData();
    Object.keys(reqData).forEach((key) => {
      key === 'users'
        ? reqData[key].forEach((user: string, i: number) => {
            formData.append(`users[${i}]`, user);
          })
        : key === 'files'
        ? reqData[key].forEach((file: any) => {
            formData.append(key, file);
          })
        : key === 'deleteFileIndex'
        ? reqData[key].forEach((id: any, i: number) => {
            formData.append(`deleteFileIndex[${i}]`, id);
          })
        : formData.append(key, reqData[key]);
    });
    return formData;
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  const onClickAddSchedule = (saveType: any) => {
    setIsSaveClicked(true);
    const setData = {
      cCd: userInfo.hCd,
      smName: selectEvent.smName,
      sDate: dateTime.start.replaceAll('-', ''),
      fDate: dateTime.end.replaceAll('-', ''),
      sTime: setTimeFormat(selectedTimeStart),
      fTime: setTimeFormat(selectedTimeEnd),
      alldayYn: selectEvent.alldayYn,
      contents: selectEvent.contents,
      completeYn: selectEvent.completeYn,
      travelYn: selectEvent.smGubun === '3' ? selectEvent.travelYn : 'N',
      importantYn: selectEvent.importantYn,
      smGubun: selectEvent.smGubun,
      users: selectEvent.smGubun === '2' || selectEvent.smGubun === '3' ? users.map((el: any) => el.value) : [],
      hsCd: `${hsCd1.hsCd1}${hsCd2.hsCd2}`,
      csCd: selectEvent.smGubun === '3' ? csCd.csCd : '',
      ccCd: ccCd.ccCd,
      dId: selectEvent.dCd,
      isPaid: isPaid || 'Y',
      wGubun: selectEvent.dGubun.join('') !== '' ? selectEvent.wGubun : '',
      dGubun: selectEvent.dGubun.sort((a: string, b: string) => +a - +b).join('|') || [],
      address: address.address1,
      latitude: address.latitude,
      longitude: address.longitude,
      files: selectEvent.fileList.map((el: IFile) => el.file), // 파일 리스트
      siteManagerName,
      siteManagerPhone,
      siteManagerPartname,
      // ...(selectEvent.isRepeatSchedule ? { orgSdate: selectEvent.orgSdate, orgStime: selectEvent.orgStime, orgFdate: selectEvent.orgFdate, orgFtime: selectEvent.orgFtime } : {}),
    };

    const newRowRequired: string[] = ['smName'];
    // const newRowRequired: string[] = setData.travelYn === 'Y' ? ['smName', 'siteManagerName', 'siteManagerPhone'] : ['smName'];
    const check: boolean = newRowRequired.some((el: string) => (setData as any)[el] === '');

    // 반복일정인 경우 일자와 반복 요일이 일치하는 지 체크
    if (selectEvent.loopYn === 'Y') {
      const startDate = new Date(dateTime.start);
      const endDate = new Date(dateTime.end);

      // 시작일과 종료일의 차이 계산 (일 단위)
      const diffTime = endDate.getTime() - startDate.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // 7일 이하인 경우에만 요일 체크 수행
      if (diffDays <= 7) {
        // 시작일부터 종료일까지 각 날짜의 요일이 선택된 요일 목록에 포함되는지 확인
        const curDate = new Date(startDate);
        let hasValidDay = false; // 선택된 요일이 하나라도 있는지 확인하는 플래그

        while (curDate <= endDate) {
          const currentDay = curDate.getDay().toString(); // 0(일요일) ~ 6(토요일)

          // 현재 날짜의 요일이 선택된 요일 목록에 있는 경우
          if (selectEvent.dGubun.includes(currentDay)) {
            hasValidDay = true;
            break; // 하나라도 발견되면 더 이상 체크할 필요 없음
          }

          // 다음 날짜로 이동
          curDate.setDate(curDate.getDate() + 1);
        }

        // 선택된 요일이 하나도 없는 경우
        if (!hasValidDay) {
          toast.warning(t('선택한 기간에 해당하는 반복 요일이 없습니다.'));
          return false;
        }
      }
    }

    if (check) {
      toast.warning(t('필수입력값을 모두 입력하세요'));
      return false;
    }
    // 기간이 같을 때 또는 반복일 경우에는 종료시간이 시작시간보다 클 경우
    if (
      (dateTime.start === dateTime.end && isTimeChecked({ start: selectedTimeStart, end: selectedTimeEnd })) ||
      (selectEvent.loopYn === 'Y' && isTimeChecked({ start: selectedTimeStart, end: selectedTimeEnd }))
    ) {
      toast.warning(t('시작시간이 종료시간보다 클 수 없습니다'));
      return false;
    }

    // // 출장일정 참가자 목록에 인원이 없는 경우 예외처리
    // if (selectEvent.travelYn === 'Y' && setData.users.length === 0) {
    //   toast.warning(t('출장 일정의 경우 참가자가 있어야 합니다'));
    //   return false;
    // }

    const req = getReq({ ...setData });

    isNewSchedule ? handleAddEvent(req) : updateEventMutation.mutate([selectEvent, saveType]);
    return false;
  };
  // 일정 추가 핸들러
  const handleAddEvent = async (newEventData: any) => {
    setIsNewSchedule(true); // 신규등록

    try {
      await addSchedule(newEventData);
    } catch (error) {
      console.error('일정 추가 중 오류 발생:', error);
    }
  };
  const { data: companyUser } = useQuery(['/user/company_tavel', userInfo.hCd], () => getCompanyUserAPI(), { enabled: !!userInfo.hCd && !!userInfo.sCd }); // 사내 사용자
  const { data: userPartList } = useQuery(['/user/company/part_tavel', userInfo.hCd], () => getCompanyUserPartAPI(), { enabled: !!userInfo.hCd }); // 사내 사용자
  const { data: scheduleInfoList } = useQuery(['/schedule/set/user', userInfo.hCd], () => getScheduleInfo(), { enabled: !!userInfo.hCd && !!userInfo.sCd }); // 사내 사용자

  // isLoading은 최초 로드시에만 true이므로 검색(refetch) 시에도 로딩 표시가 되도록 isFetching 포함
  const loading = isLoadingSchedules || isFetchingSchedules || !tableState || columns === null;

  return (
    <>
      <div className='content-container oneColumn'>
        <Root loading={loading}>
          <SearchOptions>
            {size.innerSize.W > 1024 ? (
              <>
                <div className='inputsWrapper'>
                  <div className='inputForm-row-fit'>
                    <span className='calendarLabel'>{t('기간')}</span>
                    <div className='inputForm-col'>
                      <div className='flex-basic'>
                        <input id='startInput' type='text' value={userInputDateRange.start} onChange={(e) => onChangeRangeInput(e, 'start', setDateRange, setUserInputDateRange)} />
                        <span className='inputDash'> ~ </span>
                        <input id='endInput' type='text' value={userInputDateRange.end} onChange={(e) => onChangeRangeInput(e, 'end', setDateRange, setUserInputDateRange)} />
                      </div>
                      {visibleRangePicker && (
                        <div className='rangePickerWrapper' style={{ left: '32%' }}>
                          <RangePicker state={dateRange} setState={setDateRange} setVisible={setVisibleRangePicker} />
                        </div>
                      )}
                    </div>
                    <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                      <span className='material-symbols-rounded'>calendar_month</span>
                    </button>
                  </div>
                  <div className='inputForm-row withLabel'>
                    {/* <label htmlFor='progressType'>{t('현장')}</label> */}
                    <SearchSelectBoxSm
                      options={headInfoList || []}
                      defaultOption={searchHsCd1.cdName || t('본사 선택')}
                      state={searchHsCd1}
                      setState={setSearchHsCd1}
                      stateKey='hsCd1'
                      codeKey='cdName'
                      initiateKey={searchHsCd1.hsCd1}
                      optionHeight='height-md'
                    />
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <Input
                        placeholder={t('통합검색')}
                        label=''
                        type='text'
                        id='search'
                        name='search'
                        state={searchOption}
                        setState={setSearchOption}
                        onKeyDown={onEnterKeyDown}
                        inputRef={smNameRef}
                        clearable
                      />
                    </div>
                  </div>
                  <ShortcutButton icon='search' buttonText='검색' shortcut='' onClick={onClickSearch} />
                  <InitBtn onClick={onClickInitiateSearchOption}>{t('초기화')}</InitBtn>
                </div>
                <div className='inputsWrapper'>
                  <div className='secondSearchOption'>
                    <div className='flex-basic textBtnGroup'>
                      <div className='searchResult'>
                        {t('총')}
                        <span>{tableState?.length || 0}</span>
                        {t('개')}
                      </div>
                      <div className='inputForm-row'>
                        <div className='inputForm-col withLabelComCf'>
                          <label htmlFor='useYn'>{t('보기 설정')}</label>
                          <SelectBox
                            options={listCountComCdList}
                            defaultOption={listCount.cdName}
                            state={listCount}
                            setState={setListCount}
                            stateKey={LIST_COUNT}
                            initiateKey={listCount[LIST_COUNT]}
                            setTableLinesAPI={patchUserMenuAPI}
                            optionHeight='height-sm'
                            rsearch
                          />
                        </div>
                      </div>
                    </div>
                    {/* <div className='flex-basic iconBtnGroup'>{renderExcelBtn()}</div> */}
                  </div>
                </div>
              </>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                    <div className='inputForm-col'>
                      <Input
                        placeholder={t('검색')}
                        label=''
                        type='search'
                        id='search'
                        name='search'
                        state={searchOption}
                        setState={setSearchOption}
                        inputRef={smNameRef}
                        onKeyDown={onEnterKeyDown}
                        ico='search'
                      />
                    </div>
                  </div>
                  <BtnGhost onClick={onClickFilter} className='filter-btn'>
                    <span className='material-symbols-rounded'>filter_alt</span>
                  </BtnGhost>
                </div>
                {/* <div className='inputsWrapper'>
                  <div className='flex-basic iconBtnGroup'>{renderExcelBtn()}</div>
                </div> */}
              </div>
            )}
          </SearchOptions>
          <TuiGridWrapper componentRef={componentRef}>
            {columns && (
              <TuiGrid
                data={tableState}
                // filterTableState={filterTableState}
                // setFilterTableState={setFilterTableState}
                columns={columns}
                perPage={Number(listCount[LIST_COUNT])}
                excelBtn={excelBtn}
                setExcelBtn={setExcelBtn}
                usePagenation
                onClickRow={onClickRow}
                scrollX
                height={tuiHeight}
                rowKey={rowKey}
                fileName='일정 관리'
                eListYn={eListYn}
                eExcelYn={eExcelYn}
                frozenCount={frozenCount}
                setRowKey={setRowKey}
                idxKey='smIdx'
              />
            )}
          </TuiGridWrapper>
        </Root>
      </div>
      {loading && (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'serviceReport' && <ServiceReportModal openModal={openModal} setOpenModal={setOpenModal} state={openModal.state} />}
      </Portal>

      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{t('기간')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={userInputDateRange.start} onChange={(e) => onChangeRangeInput(e, 'start', setDateRange, setUserInputDateRange)} />
                    <span className='inputDash'> ~ </span>
                    <input id='endInput' type='text' readOnly value={userInputDateRange.end} onChange={(e) => onChangeRangeInput(e, 'end', setDateRange, setUserInputDateRange)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper' style={{ left: '32%' }}>
                      <RangePicker state={dateRange} setState={setDateRange} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    state={listCount}
                    setState={setListCount}
                    stateKey={LIST_COUNT}
                    initiateKey={listCount[LIST_COUNT]}
                    setTableLinesAPI={patchUserMenuAPI}
                    optionHeight='height-sm'
                    rsearch
                  />
                </div>
              </div>
              <div className='filter-option-row'>
                <label htmlFor='progressType'>{t('현장')}</label>
                <div className='inputForm-col'>
                  <SearchSelectBoxSm
                    options={headInfoList || []}
                    defaultOption={searchHsCd1.cdName || t('본사 선택')}
                    state={searchHsCd1}
                    setState={setSearchHsCd1}
                    stateKey='hsCd1'
                    codeKey='cdName'
                    initiateKey={searchHsCd1.hsCd1}
                    optionHeight='height-md'
                    filterbar='filter-1-center'
                  />
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='search'>{t('검색')}</label>
                  <div className='inputForm-col'>
                    <Input label='' type='text' id='search' name='search' state={searchOption} setState={setSearchOption} inputRef={userNameRef} />
                  </div>
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </>
  );
};

export default ReportList;
